import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    TextField,
    Alert,
    Paper,
    LinearProgress,
    Chip,
    Divider,
} from "@mui/material";
import PurpleButton from "../../../components/LayoutComponents/PurpleButton";
import LoadingCircle from "../../../components/LayoutComponents/LoadingCircle";
import theme from "../../../components/LayoutComponents/theme";
import { triggerPdlIngestion, getPdlJobStatus, checkRunningPdlJob, getRecentPdlRuns } from "../../../remote/pdlIngestionRequest";

function PdlIngestion({ session }) {
    const [isRunning, setIsRunning] = useState(false);
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [maxFiles, setMaxFiles] = useState(1);
    const [error, setError] = useState(null);
    const [isCheckingRunning, setIsCheckingRunning] = useState(true);
    const [recentRuns, setRecentRuns] = useState([]);
    const [loadingRecentRuns, setLoadingRecentRuns] = useState(true);

    // Check for running job on mount
    useEffect(() => {
        const checkForRunningJob = async () => {
            try {
                const response = await checkRunningPdlJob(session.token);
                const data = response.data;
                if (data.running && data.jobId) {
                    setJobId(data.jobId);
                    setJobStatus({ status: data.status, startedAt: data.startedAt });
                    setIsRunning(true);
                }
            } catch (err) {
                // Silently fail - user can still start a new job
                console.error("Failed to check for running job:", err);
            } finally {
                setIsCheckingRunning(false);
            }
        };

        checkForRunningJob();
    }, [session.token]);

    // Fetch recent runs on mount and after job completes
    const fetchRecentRuns = useCallback(async () => {
        try {
            const response = await getRecentPdlRuns(session.token);
            setRecentRuns(response.data);
        } catch (err) {
            console.error("Failed to fetch recent runs:", err);
        } finally {
            setLoadingRecentRuns(false);
        }
    }, [session.token]);

    useEffect(() => {
        fetchRecentRuns();
    }, [fetchRecentRuns]);

    // Refresh recent runs when a job completes
    useEffect(() => {
        if (!isRunning && jobStatus && (jobStatus.status === "COMPLETED" || jobStatus.status === "FAILED")) {
            fetchRecentRuns();
        }
    }, [isRunning, jobStatus, fetchRecentRuns]);

    const startIngestion = async () => {
        setError(null);
        setJobStatus(null);
        setIsRunning(true);

        try {
            const response = await triggerPdlIngestion(session.token, maxFiles);
            const data = response.data;
            setJobId(data.jobId);
            setJobStatus({ status: data.status });
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Failed to start ingestion");
            setIsRunning(false);
        }
    };

    const pollStatus = useCallback(async () => {
        if (!jobId || !session.token) return;

        try {
            const response = await getPdlJobStatus(session.token, jobId);
            const data = response.data;
            setJobStatus(data);

            if (data.status === "COMPLETED" || data.status === "FAILED") {
                setIsRunning(false);
                if (data.status === "FAILED" && data.errorLog) {
                    setError(data.errorLog);
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Failed to fetch status");
            setIsRunning(false);
        }
    }, [jobId, session.token]);

    // Polling effect
    useEffect(() => {
        if (!jobId || !isRunning) return;

        const interval = setInterval(pollStatus, 10000);
        return () => clearInterval(interval);
    }, [jobId, isRunning, pollStatus]);

    const getStatusColor = (status) => {
        switch (status) {
            case "COMPLETED":
                return "success";
            case "FAILED":
                return "error";
            case "IN_PROGRESS":
                return "info";
            case "PENDING":
                return "warning";
            case "CANCELLED":
                return "default";
            default:
                return "info";
        }
    };

    const getStatusChipColor = (status) => {
        switch (status) {
            case "COMPLETED":
                return "success";
            case "FAILED":
                return "error";
            case "IN_PROGRESS":
                return "info";
            case "PENDING":
                return "warning";
            case "CANCELLED":
                return "default";
            default:
                return "default";
        }
    };

    return (
        <Box sx={{ maxWidth: 600, mx: "auto", p: 3 }}>
            <Typography
                variant="h5"
                sx={{ mb: 3, color: theme.colors.purple, textAlign: "center" }}
            >
                PDL Data Ingestion
            </Typography>

            {isCheckingRunning ? (
                <Paper elevation={2} sx={{ p: 3, mb: 3, textAlign: "center" }}>
                    <LoadingCircle size={40} />
                    <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                        Checking for running jobs...
                    </Typography>
                </Paper>
            ) : (
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                    Trigger a job to pull new job postings from PDL data stored in S3.
                    The process runs asynchronously on the server.
                </Typography>

                <TextField
                    label="Max Files to Process"
                    type="number"
                    value={maxFiles}
                    onChange={(e) => setMaxFiles(Math.max(1, parseInt(e.target.value) || 1))}
                    disabled={isRunning}
                    size="small"
                    sx={{ mb: 1, width: 200 }}
                    inputProps={{ min: 1 }}
                />

                {maxFiles > 1 && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="body2">
                            Running with more than 1 file may slow down website performance and is not recommended at this time.
                        </Typography>
                    </Alert>
                )}

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <PurpleButton
                        label={isRunning ? "Ingestion Running..." : "Start PDL Ingestion"}
                        onClick={startIngestion}
                        disabled={isRunning}
                    />
                </Box>
            </Paper>
            )}

            {/* Loading/Progress Section */}
            {isRunning && (
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <LoadingCircle size={40} />
                        <Typography variant="body1">
                            {jobStatus?.status === "PENDING" && "Job pending..."}
                            {jobStatus?.status === "IN_PROGRESS" && "Processing files..."}
                        </Typography>
                    </Box>
                    <LinearProgress color="secondary" />

                    {/* Progress Stats */}
                    {jobStatus && jobStatus.status === "IN_PROGRESS" && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Processed: {jobStatus.jobsProcessed || 0} |
                                Created: {jobStatus.jobsCreated || 0} |
                                Skipped: {jobStatus.jobsSkipped || 0} |
                                Failed: {jobStatus.jobsFailed || 0}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            )}

            {/* Result Section */}
            {!isRunning && jobStatus && (jobStatus.status === "COMPLETED" || jobStatus.status === "FAILED") && (
                <Alert severity={getStatusColor(jobStatus.status)} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                        {jobStatus.status === "COMPLETED" ? "Ingestion Completed!" : "Ingestion Failed"}
                    </Typography>
                    <Typography variant="body2">
                        Jobs Processed: {jobStatus.jobsProcessed || 0}<br />
                        Jobs Created: {jobStatus.jobsCreated || 0}<br />
                        Jobs Skipped: {jobStatus.jobsSkipped || 0}<br />
                        Jobs Failed: {jobStatus.jobsFailed || 0}
                    </Typography>
                    {jobStatus.startedAt && (
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            Started: {new Date(jobStatus.startedAt).toLocaleString()}
                        </Typography>
                    )}
                    {jobStatus.completedAt && (
                        <Typography variant="caption" display="block">
                            Completed: {new Date(jobStatus.completedAt).toLocaleString()}
                        </Typography>
                    )}
                </Alert>
            )}

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                        Error
                    </Typography>
                    <Typography variant="body2">{error}</Typography>
                </Alert>
            )}

            {/* Recent Runs Section */}
            <Divider sx={{ my: 3 }} />
            <Typography
                variant="h6"
                sx={{ mb: 2, color: theme.colors.purple }}
            >
                Recent Runs
            </Typography>

            {loadingRecentRuns ? (
                <Box sx={{ textAlign: "center", py: 2 }}>
                    <LoadingCircle size={30} />
                </Box>
            ) : recentRuns.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                    No recent runs found.
                </Typography>
            ) : (
                recentRuns.map((run) => (
                    <Paper key={run.id} elevation={1} sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                            <Typography variant="subtitle2">
                                Job #{run.id}
                            </Typography>
                            <Chip
                                label={run.status}
                                size="small"
                                color={getStatusChipColor(run.status)}
                            />
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Started: {new Date(run.startedAt).toLocaleString()}
                        </Typography>
                        {run.completedAt && (
                            <Typography variant="caption" color="text.secondary" display="block">
                                Completed: {new Date(run.completedAt).toLocaleString()}
                            </Typography>
                        )}
                        {(run.status === "COMPLETED" || run.status === "FAILED" || run.status === "CANCELLED") && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                    Processed: {run.jobsProcessed ?? "-"} |
                                    Created: {run.jobsCreated ?? "-"} |
                                    Skipped: {run.jobsSkipped ?? "-"} |
                                    Failed: {run.jobsFailed ?? "-"}
                                </Typography>
                            </Box>
                        )}
                        {run.errorLog && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                                <Typography variant="caption">{run.errorLog}</Typography>
                            </Alert>
                        )}
                    </Paper>
                ))
            )}
        </Box>
    );
}

export default PdlIngestion;
